import {
  IconButton,
  Spacer,
  Split,
  SplitSection,
  SvgBell,
  SvgMenu,
} from '@loomhq/lens';
import cn from 'classnames';
import Mousetrap from 'mousetrap';
import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { openModal } from 'actions/modals';
import { DESTINATION_SEARCH_MODAL } from 'constants/modalTypes';
import { MOD_K } from 'constants/mousetrap';
import { SEARCH_ACCESS } from 'constants/scopes';
// Remove when notifications moves to sidebar
import { ACCOUNT_NOTIFICATIONS } from 'constants/scopes';

import useDowntimeBanner from 'hooks/useDowntimeBanner';
import useTheme from 'hooks/useTheme';
import { selectInTrimMode } from 'selectors/shareVideo';
import { selectUserIsLogged } from 'selectors/user';

import { AnonymousHeader } from './anonymous-header';

import SearchBar from 'components/destination-search/search-bar';
import DownloadProgress from 'components/download-progress';
import HelpBubble from 'components/help-bubble';
import HideDuringDowntime from 'components/hide-during-downtime';
import HeaderPortalDestination from 'components/layout/header/portal-destination';
import UpgradePrompt from 'components/layout/header/UpgradePrompt';
import { BubbleMenu, ProfileBubb } from 'components/profile-bubble';
import Scopes from 'components/scopes';
import { useShouldShowWaveform } from 'components/share-video/trim-tools/hooks';
import { CompactBreadcrumb } from 'components/videos-dashboard-ta/breadcrumb';

import styles from './styles.module.less';

const Notifications = lazy(() => import('components/notifications-tray'));

type HeaderV2Props = {
  hasCompactBreadcrumb: boolean;
  setNavBarIsOpenMobile: () => void;
};

export const HeaderV2 = ({
  hasCompactBreadcrumb,
  setNavBarIsOpenMobile,
}: HeaderV2Props) => {
  const dispatch = useDispatch();
  const [menu, setMenu] = useState(null);
  const isUserLoggedIn = useSelector(selectUserIsLogged);
  const isInTrimMode = useSelector(selectInTrimMode);
  useDowntimeBanner();
  useTheme();

  const shouldShowWaveform = useShouldShowWaveform();

  useEffect(() => {
    Mousetrap.bind(MOD_K, event => {
      event.preventDefault();
      openSearch(true);
    });

    return () => {
      Mousetrap.unbind(MOD_K);
    };
  });

  const onBubbleMenuExit = useCallback(() => {
    setTimeout(() => {
      document.body.classList.remove('noscroll');
      setMenu(null);
    }, 150);
  }, [setMenu]);

  const onBubbleMenuOpen = () => setMenu('bubb');

  const openSearch = isShortcut => {
    dispatch(
      openModal(DESTINATION_SEARCH_MODAL, {
        isShortcut: Boolean(isShortcut),
      })
    );
  };

  // Header for non-logged in users
  if (!isUserLoggedIn) {
    return <AnonymousHeader setNavBarIsOpenMobile={setNavBarIsOpenMobile} />;
  }

  const isBubb = menu === 'bubb';

  return (
    <>
      <header
        className={cn(styles.header, 'sticky top:0 bgc:background mb:medium')}
      >
        {isInTrimMode && shouldShowWaveform && (
          <div
            className={cn(
              styles.headerOverlay,
              'absolute top:0 left:0 width:full height:full bgc:overlay'
            )}
          ></div>
        )}

        <DownloadProgress />

        {hasCompactBreadcrumb && (
          <HideDuringDowntime>
            <div className="sm-mr:auto">
              <CompactBreadcrumb />
            </div>
          </HideDuringDowntime>
        )}

        <ul className={cn(styles.headerBar, 'items:center')}>
          <li className={cn(styles.headerBarItem)}>
            <div className={cn(styles.mobileMenu, 'mr:auto')}>
              <Spacer right="small">
                <IconButton
                  altText="Toggle library navigation"
                  icon={<SvgMenu />}
                  onClick={() => setNavBarIsOpenMobile()}
                />
              </Spacer>
            </div>
          </li>
          <li
            className={cn(styles.searchBar, styles.headerBarItem, 'py:medium')}
          >
            <SearchBar />
          </li>
          <li
            className={cn(styles.headerBarItem, 'py:medium items:center flex')}
          >
            <HeaderPortalDestination />
          </li>
          <li className={cn(styles.headerBarItem)}>
            <HideDuringDowntime>
              <div className="p:medium">
                <Split gap="medium" wrap="nowrap">
                  <SplitSection>
                    <ul className={cn(styles.actions, 'flex items:center')}>
                      <Scopes name={ACCOUNT_NOTIFICATIONS}>
                        <li>
                          <Suspense
                            fallback={
                              <IconButton
                                altText="Loading notifications"
                                icon={<SvgBell />}
                                isDisabled
                              />
                            }
                          >
                            <Notifications />
                          </Suspense>
                        </li>
                      </Scopes>

                      <li>
                        <ProfileBubb onOpen={onBubbleMenuOpen} />
                      </li>
                    </ul>
                  </SplitSection>
                </Split>
                {isBubb && <BubbleMenu onExit={onBubbleMenuExit} />}
                {isUserLoggedIn && <HelpBubble />}
              </div>
            </HideDuringDowntime>
          </li>
        </ul>
      </header>
    </>
  );
};
